import { gql, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  UserOwnedItemDetailFragmentDoc,
  UserOwnedItemsConnectionFragmentDoc,
  UserOwnedItemsQuery,
  UserOwnedItemsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { spreadPortfoliosBySku } from '@/core/sku'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { router } = useRouter<{ userId: string }>()

  const { isFirstLoadRef } = useGetNewestData<
    UserOwnedItemsQuery,
    UserOwnedItemsQueryVariables
  >({
    query: QUERY,
    variables: { userId: router.query.userId },
    fields: [
      {
        fieldName: 'events',
        fragmentName: 'UserOwnedItemDetail',
        fragment: UserOwnedItemDetailFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore } = useQuery<
    UserOwnedItemsQuery,
    UserOwnedItemsQueryVariables
  >(QUERY, {
    variables: { userId: router.query.userId },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  return {
    state: {
      loading,
      userId: router.query.userId,
      userName: data?.user?.displayName || '',
      hasNextPage: !!data?.user?.organizationPortfolios?.pageInfo?.hasNextPage,
      portfolios: spreadPortfoliosBySku(
        data?.user?.organizationPortfolios ?? null
      ),
    },
    actions: {
      onFilter(key: number) {},
      async onFetchMore() {
        await fetchMore({
          variables: {
            userId: router.query.userId,
            pageInput: {
              cursor: data?.user?.organizationPortfolios?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
}

const QUERY = gql`
  query UserOwnedItems($type: ItemType, $pageInput: PageInput, $userId: ID) {
    user(id: $userId) {
      id
      displayName
      organizationPortfolios(type: $type, pageInput: $pageInput) {
        ...UserOwnedItemsConnection
      }
    }
  }
  ${UserOwnedItemsConnectionFragmentDoc}
`
